import { useCallback, useMemo } from 'react'

import { isFunction } from 'lodash'

import { onError, onSuccess } from '../../apiHelper'
import { Actions } from '../../events'
import { createInstitution, updateInstitution } from '../institution'
import { Institution } from '../../entities/institution'

const useInstitution = ({ user, t, callback }) => {
  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => {}, [callback])

  const create = useCallback(institution => {
    return createInstitution(user, institution).then(({ data }) => {
      safeCallback(data, Actions.CREATE)

      onSuccess(t('institution.create.success'))

      return data
    }).catch(error => {
      console.error(error)
      onError(t('institution.create.error'))
    })
  }, [user, t, safeCallback])

  const upsert = useCallback((institution, previousState) => {
    const config = institution.id === -1 ? { request: createInstitution, action: Actions.CREATE } : { request: updateInstitution, action: Actions.UPDATE }

    safeCallback(institution, config.action)

    return config.request(user, institution).then(({ data }) => {
      const newInstitution = new Institution(data)

      if (config.action === Actions.CREATE) {
        safeCallback(institution, Actions.DELETE, newInstitution, Actions.CREATE)
      } else {
        safeCallback(newInstitution, Actions.UPDATE)
      }

      onSuccess(t(`institution.${config.action.toLowerCase()}.success`))

      return newInstitution
    }).catch(reason => {
      console.error(reason)

      config.action === Actions.CREATE ? safeCallback(institution, Actions.DELETE) : safeCallback(previousState, Actions.UPDATE)

      onError(t(`institution.${config.action.toLowerCase()}.error`))
    })
  }, [t, user, safeCallback])

  const update = useCallback(institution => {
    return updateInstitution(user, institution).then(({ data }) => {
      const updatedInstitution = new Institution(data)

      safeCallback(updatedInstitution, Actions.UPDATE)

      onSuccess(t('institution.update.success'))

      return updatedInstitution
    }).catch(error => {
      console.error(error)
      onError(t('institution.update.error'))
    })
  }, [upsert])

  return { create, upsert, update }
}

export default useInstitution
